/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// Custom hook to use auth context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // only for startup auth check
  const [error, setError] = useState(null);

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = authAPI.getToken();
        if (token) {
          // Verify token is still valid by fetching user profile
          const { studentAPI } = await import('../services/api');
          const userData = await studentAPI.getProfile();
          setUser(userData.student);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Token is invalid, remove it
        authAPI.logout();
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsInitializing(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = useCallback(async (cin, password, totpCode) => {
    // NOTE: do NOT set isInitializing here — that would unmount LoginForm and reset its local state
    try {
      setError(null);
      const data = await authAPI.login(cin, password, totpCode);

      // Server asks for 2FA code — token not issued yet
      if (data.requires2FA) {
        return { requires2FA: true };
      }

      // Set authenticated using login response data
      const studentFromLogin = data.student;
      setUser(studentFromLogin);
      setIsAuthenticated(true);

      return { success: true, user: studentFromLogin };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(() => {
    authAPI.logout();
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading: isInitializing, // keep isLoading in value for ProtectedRoute/App compatibility
    error,
    login,
    logout,
    clearError
  }), [user, isAuthenticated, isInitializing, error, login, logout, clearError]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export everything at the bottom
export { useAuth, AuthProvider };